/**
 * SIP Connection Recovery Service
 *
 * Handles automatic SIP connection recovery when configuration loading fails.
 * Uses exponential backoff with jitter to retry config loading and re-establish SIP connections.
 */

import { NODE_ENV_LOCAL_OR_DEVELOPMENT } from 'Constants/env';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { useNotificationStore } from 'Modules/notification/store';
import { SoftphoneCoordinator } from 'Modules/softphone/utils/softphoneCoordinator';
import type { RootStore } from 'Stores/RootStore';
import {
  retryWithBackoff,
  type BackoffOptions,
} from 'Utils/exponentialBackoff';
import { getLogger, Logger } from 'Utils/logger';

/**
 * Configuration options for the SIP recovery service
 */
export interface SipRecoveryOptions extends BackoffOptions {
  /** Whether to log recovery attempts to console */
  enableConsoleLogging?: boolean;
}

/**
 * Recovery attempt information
 */
export interface RecoveryAttempt {
  timestamp: number;
  attempt: number;
  error: string;
  nextRetryIn: number;
}

/**
 * Service for managing SIP connection recovery when config loading fails
 */
export class SipConnectionRecoveryService {
  private rootStore: RootStore;
  private recoveryTimer?: NodeJS.Timer;
  private options: Required<SipRecoveryOptions>;
  private logger: Logger;

  @observable public isRecovering = false;
  @observable public lastRecoveryAttempt: RecoveryAttempt | null = null;
  @observable public recoveryAttemptCount = 0;

  constructor(rootStore: RootStore, options: SipRecoveryOptions = {}) {
    this.rootStore = rootStore;
    this.options = {
      initialDelay: 500, // Start with 0.5 seconds for config retries
      maxDelay: 16000, // Cap at 16 seconds
      maxAttempts: 10, // Allow 10 attempts
      backoffFactor: 2, // 2x growth
      jitter: true,
      timeout: 20000,
      enableConsoleLogging: NODE_ENV_LOCAL_OR_DEVELOPMENT,
      ...options,
    };
    this.logger = getLogger();

    makeObservable(this);
  }

  /**
   * Initiates config recovery with automatic SIP reconnection
   */
  @action
  public async startConfigRecovery(): Promise<void> {
    // Guard: this service only handles legacy SIP config recovery.
    const coordinator = SoftphoneCoordinator.getInstance();
    const personId = this.rootStore.personStore.loggedInPersonId;
    if (coordinator.readPreferenceSync(personId)) {
      this.consoleLog(
        'SipConnectionRecoveryService: new softphone is active — skipping legacy config recovery'
      );
      return;
    }

    if (this.isRecovering) {
      this.consoleLog(
        'SipConnectionRecoveryService already recovering, skipping'
      );
      return;
    }

    this.setIsRecovering(true);
    this.recoveryAttemptCount = 0;

    this.consoleLog('SipConnectionRecoveryService starting config recovery');
    this.logger.info('SipConnectionRecoveryService starting config recovery');

    try {
      await retryWithBackoff({
        ...this.options,
        operation: () => this.loadConfigWithRecovery(),
        shouldRetry: (error: Error, attempt: number) =>
          this.shouldRetryConfigLoad(error, attempt),
        onRetry: (error: Error, attempt: number, nextDelay: number) =>
          this.onRetryAttempt(error, attempt, nextDelay),
      });

      // If we get here, config loading succeeded
      await this.onRecoverySuccess();
    } catch (error) {
      // All retry attempts failed
      this.onRecoveryFailure(error as Error);
    }
  }

  /**
   * Stops any ongoing recovery attempts
   */
  @action
  public stopRecovery(): void {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = undefined;
    }

    this.setIsRecovering(false);
    this.recoveryAttemptCount = 0;
    this.lastRecoveryAttempt = null;

    this.consoleLog('SipConnectionRecoveryService recovery stopped');
  }

  /**
   * Attempts to load config and handle the result
   */
  private async loadConfigWithRecovery(): Promise<void> {
    this.consoleLog('SipConnectionRecoveryService attempting to load config');

    // Clear any existing config to force a fresh load
    runInAction(() => {
      this.rootStore.configStore.signedInPersonConfig = null;
      this.rootStore.configStore.signedInPersonConfigLoaded = false;
    });

    try {
      // Attempt to load config
      await this.rootStore.configStore.loadConfig();

      // Check if config loaded successfully
      if (
        this.rootStore.configStore.signedInPersonConfig?.state === 'rejected'
      ) {
        throw this.rootStore.configStore.signedInPersonConfig?.value;
      }

      this.consoleLog(
        'SipConnectionRecoveryService config loaded successfully'
      );
    } catch (error) {
      const message = `Config loading failed: ${(error as Error).message}`;
      this.consoleLog('SipConnectionRecoveryService', message);

      throw error;
    }
  }

  /**
   * Determines if a config load error should trigger a retry
   */
  private shouldRetryConfigLoad(error: Error, attempt: number): boolean {
    // We may want to retry only in some specific cases, but for now, we'll always retry.

    this.consoleLog('SipConnectionRecoveryService retrying error', {
      message: error.message,
      attempt,
    });
    return true;
  }

  /**
   * Called on each retry attempt
   */
  private onRetryAttempt(
    error: Error,
    attempt: number,
    nextDelay: number
  ): void {
    runInAction(() => {
      this.recoveryAttemptCount = attempt + 1;
      this.lastRecoveryAttempt = {
        timestamp: Date.now(),
        attempt: attempt + 1,
        error: error.message,
        nextRetryIn: nextDelay,
      };
    });

    this.consoleLog('SipConnectionRecoveryService retry attempt', {
      attempt: attempt + 1,
      error: error.message,
      nextDelay,
    });
    this.logger.info('SipConnectionRecoveryService retry attempt', {
      attempt: attempt + 1,
      error: error.message,
      nextDelay,
    });
  }

  /**
   * Called when recovery succeeds
   */
  private onRecoverySuccess(): void {
    this.consoleLog('SipConnectionRecoveryService recovery succeeded');
    this.logger.info('SipConnectionRecoveryService recovery succeeded');

    this.setIsRecovering(false);

    // Trigger SIP reconnection
    try {
      if (this.rootStore.phoneStore?.runRegister) {
        this.consoleLog(
          'SipConnectionRecoveryService triggering full SIP re-registration with fresh config'
        );
        this.rootStore.phoneStore.runRegister();
      }
    } catch (error) {
      this.consoleLog(
        'SipConnectionRecoveryService SIP re-registration failed',
        error
      );

      this.logger.error(
        'SipConnectionRecoveryService SIP re-registration failed',
        error
      );
    }

    this.recoveryAttemptCount = 0;
    this.lastRecoveryAttempt = null;
  }

  /**
   * Called when all recovery attempts fail
   */
  private onRecoveryFailure(error: Error): void {
    this.consoleLog(
      'SipConnectionRecoveryService recovery failed after all attempts',
      error
    );
    this.logger.error(
      'SipConnectionRecoveryService recovery failed after all attempts',
      error
    );

    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error loading the app',
      message: 'We couldn’t load the app content. Please try again later.',
    });

    this.setIsRecovering(false);
  }

  @action
  private setIsRecovering(recovering: boolean): void {
    this.isRecovering = recovering;
  }

  /**
   * Conditional logging helper that only logs when enableLogging is true
   */
  private consoleLog(message: string, ...optionalParams: unknown[]): void {
    if (this.options.enableConsoleLogging) {
      console.log(message, ...optionalParams);
    }
  }
}
